import {getEntityTypeLabel} from '../../../utils/entityTypeMeta';
import {getEntityTypeSuggestions} from './computeEntityFilterFacets';
import {
    compactEmptiedPropertyFilterRows,
    createDefaultEntityFilters,
    createEmptyPropertyFilterRow,
    createEntityTypeFilterRow,
    getMatchingEntityIds,
} from './evaluateEntityPropertyFilters';

/** Citizenship / country Wikidata ids — shown first on the OG chip rail. */
export const COUNTRY_FACET_PROPERTY_KEYS = ['P27', 'P17', 'P495', 'P1532'];

/** Occupation / office — next after country chips. */
export const OCCUPATION_FACET_PROPERTY_KEYS = ['P106', 'P39'];

/**
 * Human-readable group labels for known property keys shown in the 3-zone filter bar.
 * Keys not listed here fall into their chip.group value (from propertyOptions) or "Other attributes".
 */
const PROPERTY_GROUP_LABELS = {
    P27: 'Nationality',
    P17: 'Country',
    P495: 'Country of origin',
    P1532: 'Country for sport',
    P106: 'Occupation',
    P39: 'Position held',
    P21: 'Gender',
    P31: 'Instance of',
    P279: 'Subclass of',
    P569: 'Born',
    P570: 'Died',
    P19: 'Place of birth',
    P20: 'Place of death',
    P102: 'Political party',
    P108: 'Employer',
    P131: 'Located in',
    P551: 'Residence',
};

const PROPERTY_GROUP_ORDER = [
    'Instance of',
    'Subclass of',
    'Nationality',
    'Country',
    'Country of origin',
    'Country for sport',
    'Occupation',
    'Position held',
    'Political party',
    'Employer',
    'Gender',
    'Born',
    'Died',
    'Place of birth',
    'Place of death',
    'Located in',
    'Residence',
    'Other attributes',
];

/**
 * Split a flat chip array (from {@link collectChartFacetChips}) into an ordered
 * array of `{ groupLabel, chips }` objects for the 3-zone filter bar.
 *
 * Entity-type chips (kind === 'entityType') are returned in a dedicated first
 * group labeled "Entity type".  Property chips are bucketed by their known
 * property group label, with unknown keys collected under "Other attributes".
 *
 * @param {Array<{id: string, kind: string, token: string, label: string, group: string, propertyKey?: string}>} chips
 * @returns {Array<{groupLabel: string, chips: Array}>}
 */
export function groupFacetChips(chips) {
    const typeChips = chips.filter((c) => c.kind === 'entityType');
    const propChips = chips.filter((c) => c.kind !== 'entityType');

    // Bucket by resolved group label
    const buckets = new Map();
    for (const chip of propChips) {
        const label = PROPERTY_GROUP_LABELS[chip.propertyKey] || chip.group || 'Other attributes';
        if (!buckets.has(label)) buckets.set(label, []);
        buckets.get(label).push(chip);
    }

    // Sort buckets by canonical order, unknowns after
    const sortedBuckets = [...buckets.entries()].sort(([a], [b]) => {
        const ai = PROPERTY_GROUP_ORDER.indexOf(a);
        const bi = PROPERTY_GROUP_ORDER.indexOf(b);
        if (ai === -1 && bi === -1) return a.localeCompare(b);
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
    });

    const result = [];
    if (typeChips.length > 0) {
        result.push({groupLabel: 'Entity type', chips: typeChips});
    }
    for (const [groupLabel, groupChips] of sortedBuckets) {
        result.push({groupLabel, chips: groupChips});
    }
    return result;
}

const PRIORITY_PROPERTY_KEYS = [
    ...COUNTRY_FACET_PROPERTY_KEYS,
    ...OCCUPATION_FACET_PROPERTY_KEYS,
];

const MAX_PROPERTY_CHIPS = 12;
const MAX_FACET_LABEL_LENGTH = 28;

function normalizeComparableString(raw) {
    if (raw == null) return '';
    return String(raw).trim().toLowerCase();
}

function cloneFilters(filters) {
    if (!Array.isArray(filters) || filters.length === 0) {
        return createDefaultEntityFilters();
    }
    return filters.map((row) => ({
        ...row,
        values: Array.isArray(row?.values) ? [...row.values] : [],
    }));
}

function entityTypeRow(filters) {
    return (filters || []).find((row) => row?.kind === 'entityType') || createEntityTypeFilterRow();
}

function propertyKeyForItem(item) {
    if (typeof item?.propertyId === 'string' && item.propertyId.trim()) {
        return item.propertyId.trim();
    }
    if (typeof item?.propertyLabel === 'string' && item.propertyLabel.trim()) {
        return item.propertyLabel.trim();
    }
    return null;
}

function primaryValueTokenForItem(item) {
    if (typeof item?.valueLabel === 'string' && item.valueLabel.trim()) {
        return item.valueLabel.trim();
    }
    if (typeof item?.value === 'string' && item.value.trim()) {
        return item.value.trim();
    }
    if (typeof item?.valueEntityId === 'string' && item.valueEntityId.trim()) {
        return item.valueEntityId.trim();
    }
    return null;
}

function valuesInclude(values, token) {
    const needle = normalizeComparableString(token);
    return (values || []).some((value) => normalizeComparableString(value) === needle);
}

function toggleValue(values, token) {
    if (valuesInclude(values, token)) {
        return (values || []).filter((value) => normalizeComparableString(value) !== normalizeComparableString(token));
    }
    return [...(values || []), token];
}

function upsertEntityTypeValues(filters, values) {
    const next = cloneFilters(filters);
    const existing = next.find((row) => row.kind === 'entityType');
    if (existing) {
        existing.values = values;
        return next;
    }
    return [{...createEntityTypeFilterRow(), values}, ...next];
}

function upsertPropertyValues(filters, propertyKey, values) {
    const trimmedKey = typeof propertyKey === 'string' ? propertyKey.trim() : '';
    if (!trimmedKey) return cloneFilters(filters);

    const next = cloneFilters(filters);
    const existing = next.find((row) => (
        row.kind !== 'entityType'
        && normalizeComparableString(row.propertyKey) === normalizeComparableString(trimmedKey)
    ));
    if (existing) {
        existing.kind = 'property';
        existing.propertyKey = trimmedKey;
        existing.operator = existing.operator || '=';
        existing.values = values;
        if (values.length === 0) {
            return compactEmptiedPropertyFilterRows(next, filters);
        }
        return next;
    }

    const empty = next.find((row) => row.kind === 'property' && !String(row.propertyKey || '').trim());
    if (empty) {
        empty.kind = 'property';
        empty.propertyKey = trimmedKey;
        empty.operator = empty.operator || '=';
        empty.values = values;
        return next;
    }

    next.push({
        ...createEmptyPropertyFilterRow(),
        id: `property-${trimmedKey}`,
        propertyKey: trimmedKey,
        values,
    });
    return next;
}

/**
 * Toggle one OG chip into the same `entityFilters` rows the Entities-tab editor uses.
 * Same group (row) = OR; different groups = AND.
 * Adding a chip that would match nobody replaces the current filters with that
 * chip only — instead of an empty chart or a prune that clears everything.
 *
 * @param {Array<object>} filters
 * @param {{kind: 'entityType'|'property', token: string, propertyKey?: string}} chip
 * @param {{
 *   chartEntities?: Array<object>,
 *   propertiesByEntityId?: Record<string, Array<object>>,
 *   summariesByEntityId?: Record<string, {entityType?: string|null}>,
 *   summariesLoading?: boolean,
 * }} [matchContext]
 * @returns {Array<object>}
 */
export function toggleFacetChip(filters, chip, matchContext) {
    const token = typeof chip?.token === 'string' ? chip.token.trim() : '';
    if (!token) return cloneFilters(filters);

    const adding = !isFacetChipSelected(filters, chip);
    let next;
    if (chip.kind === 'entityType') {
        const current = entityTypeRow(filters);
        next = upsertEntityTypeValues(filters, toggleValue(current.values, token));
    } else {
        const propertyKey = typeof chip.propertyKey === 'string' ? chip.propertyKey.trim() : '';
        if (!propertyKey) return cloneFilters(filters);
        const current = (filters || []).find((row) => (
            row.kind !== 'entityType'
            && normalizeComparableString(row.propertyKey) === normalizeComparableString(propertyKey)
        ));
        const currentValues = Array.isArray(current?.values) ? current.values : [];
        next = upsertPropertyValues(filters, propertyKey, toggleValue(currentValues, token));
    }

    if (adding && intersectionIsEmpty(next, matchContext)) {
        return applyChipAsSoleFilter(chip);
    }
    return next;
}

/**
 * @param {{kind: 'entityType'|'property', token: string, propertyKey?: string}} chip
 * @returns {Array<object>}
 */
function applyChipAsSoleFilter(chip) {
    const token = typeof chip?.token === 'string' ? chip.token.trim() : '';
    if (chip.kind === 'entityType') {
        return upsertEntityTypeValues(createDefaultEntityFilters(), token ? [token] : []);
    }
    const propertyKey = typeof chip.propertyKey === 'string' ? chip.propertyKey.trim() : '';
    return upsertPropertyValues(createDefaultEntityFilters(), propertyKey, token ? [token] : []);
}

/**
 * @param {Array<object>} filters
 * @param {{
 *   chartEntities?: Array<object>,
 *   propertiesByEntityId?: Record<string, Array<object>>,
 *   summariesByEntityId?: Record<string, {entityType?: string|null}>,
 *   summariesLoading?: boolean,
 * }|null|undefined} matchContext
 * @returns {boolean}
 */
function intersectionIsEmpty(filters, matchContext) {
    if (!matchContext || !Array.isArray(matchContext.chartEntities)) {
        return false;
    }
    return getMatchingEntityIds(
        matchContext.chartEntities,
        matchContext.propertiesByEntityId,
        matchContext.summariesByEntityId,
        filters,
        {summariesLoading: matchContext.summariesLoading},
    ).size === 0;
}

/**
 * Apply assistant `{ entityFilterActions }` onto the existing filter-row model.
 *
 * @param {Array<object>} filters
 * @param {Array<{action?: string, kind?: string, propertyKey?: string, values?: string[]}>} actions
 * @returns {Array<object>}
 */
export function applyEntityFilterActions(filters, actions) {
    let next = cloneFilters(filters);
    if (!Array.isArray(actions) || actions.length === 0) {
        return next;
    }

    for (const raw of actions) {
        const action = typeof raw?.action === 'string' ? raw.action.trim().toLowerCase() : 'set';
        if (action === 'clear') {
            next = createDefaultEntityFilters();
            continue;
        }
        if (action !== 'set' && action !== 'add') continue;

        const kind = raw?.kind === 'entityType' ? 'entityType' : 'property';
        const values = (Array.isArray(raw?.values) ? raw.values : [])
            .map((value) => (value == null ? '' : String(value).trim()))
            .filter(Boolean);
        if (!values.length && action === 'set' && kind === 'entityType') {
            next = upsertEntityTypeValues(next, []);
            continue;
        }
        if (!values.length) continue;

        if (kind === 'entityType') {
            const current = entityTypeRow(next).values || [];
            next = upsertEntityTypeValues(next, action === 'add' ? [...current, ...values] : values);
            continue;
        }

        const propertyKey = typeof raw?.propertyKey === 'string' ? raw.propertyKey.trim() : '';
        if (!propertyKey) continue;
        const current = (next || []).find((row) => (
            row.kind !== 'entityType'
            && normalizeComparableString(row.propertyKey) === normalizeComparableString(propertyKey)
        ));
        const currentValues = Array.isArray(current?.values) ? current.values : [];
        next = upsertPropertyValues(
            next,
            propertyKey,
            action === 'add' ? [...currentValues, ...values] : values,
        );
    }

    return next;
}

export function isFacetChipSelected(filters, chip) {
    const token = typeof chip?.token === 'string' ? chip.token.trim() : '';
    if (!token) return false;
    if (chip.kind === 'entityType') {
        return valuesInclude(entityTypeRow(filters).values, token);
    }
    const propertyKey = typeof chip.propertyKey === 'string' ? chip.propertyKey.trim() : '';
    if (!propertyKey) return false;
    const row = (filters || []).find((candidate) => (
        candidate.kind !== 'entityType'
        && normalizeComparableString(candidate.propertyKey) === normalizeComparableString(propertyKey)
    ));
    return valuesInclude(row?.values, token);
}

function propertyLabelForKey(propertyKey, propertyOptions) {
    const match = (propertyOptions || []).find((option) => option.propertyKey === propertyKey);
    return match?.label || propertyKey;
}

function priorityRank(propertyKey) {
    const index = PRIORITY_PROPERTY_KEYS.indexOf(propertyKey);
    return index === -1 ? PRIORITY_PROPERTY_KEYS.length : index;
}

/**
 * Chip label for a KB value: drop "role, Organization" / "a / b" tails,
 * sentence-case, then cap length so the rail stays readable.
 *
 * @param {unknown} raw
 * @returns {string}
 */
export function formatFacetChipLabel(raw) {
    let text = String(raw ?? '').trim().replace(/\s+/g, ' ');
    if (!text) return '';

    const comma = text.search(/\s*,\s*/);
    if (comma > 0) {
        text = text.slice(0, comma).trim();
    }
    const slash = text.search(/\s+\/\s+/);
    if (slash > 0) {
        text = text.slice(0, slash).trim();
    }

    text = text.charAt(0).toUpperCase() + text.slice(1);
    if (text.length > MAX_FACET_LABEL_LENGTH) {
        return `${text.slice(0, MAX_FACET_LABEL_LENGTH - 1).trimEnd()}…`;
    }
    return text;
}

function isBetterFacetValue(candidate, current) {
    if (candidate.count !== current.count) {
        return candidate.count > current.count;
    }
    const rankDiff = priorityRank(candidate.propertyKey) - priorityRank(current.propertyKey);
    if (rankDiff !== 0) {
        return rankDiff < 0;
    }
    return candidate.propertyKey.localeCompare(current.propertyKey) < 0;
}

/**
 * Auto-populate OG chips from on-chart types + top property values.
 *
 * @returns {Array<{
 *   id: string,
 *   kind: 'entityType'|'property',
 *   token: string,
 *   label: string,
 *   group: string,
 *   propertyKey?: string,
 * }>}
 */
export function collectChartFacetChips({
    chartEntities = [],
    propertiesByEntityId = {},
    summariesByEntityId = {},
    filters = [],
    propertyOptions = [],
    summariesLoading = false,
} = {}) {
    const typeChips = getEntityTypeSuggestions(
        chartEntities,
        propertiesByEntityId,
        summariesByEntityId,
        [],
        null,
        {summariesLoading},
    ).map((option) => {
        // Count how many chart entities have this entity type
        const count = (chartEntities || []).filter((entity) => {
            const summary = summariesByEntityId?.[entity?.wikidataId];
            if (summary?.entityType) {
                return normalizeComparableString(summary.entityType) === normalizeComparableString(option.token);
            }
            // Fallback: check entity.entityType if present
            return normalizeComparableString(entity?.entityType) === normalizeComparableString(option.token);
        }).length;
        return {
            id: `type:${option.token}`,
            kind: 'entityType',
            token: option.token,
            label: option.label || getEntityTypeLabel(option.token) || option.token,
            group: 'Type',
            count: count || 1,
        };
    });

    const countsByProperty = new Map();
    for (const entity of chartEntities || []) {
        const entityId = entity?.wikidataId;
        if (!entityId) continue;
        for (const item of propertiesByEntityId?.[entityId] || []) {
            const propertyKey = propertyKeyForItem(item);
            const token = primaryValueTokenForItem(item);
            if (!propertyKey || !token) continue;
            if (!countsByProperty.has(propertyKey)) {
                countsByProperty.set(propertyKey, new Map());
            }
            const valueMap = countsByProperty.get(propertyKey);
            const key = normalizeComparableString(token);
            // Capture valueEntityId (Wikidata QID of the value, e.g. Q96 for Mexico)
            const valueEntityId = (typeof item?.valueEntityId === 'string' && item.valueEntityId.trim())
                ? item.valueEntityId.trim()
                : null;
            const current = valueMap.get(key);
            if (current) {
                current.count += 1;
                // Prefer a known valueEntityId over null (first-seen wins if already set)
                if (!current.valueEntityId && valueEntityId) {
                    current.valueEntityId = valueEntityId;
                }
            } else {
                valueMap.set(key, {token, count: 1, valueEntityId});
            }
        }
    }

    const bestByToken = new Map();
    for (const [propertyKey, valueMap] of countsByProperty.entries()) {
        for (const value of valueMap.values()) {
            const candidate = {propertyKey, token: value.token, count: value.count, valueEntityId: value.valueEntityId || null};
            const tokenKey = normalizeComparableString(value.token);
            const current = bestByToken.get(tokenKey);
            if (!current || isBetterFacetValue(candidate, current)) {
                bestByToken.set(tokenKey, candidate);
            }
        }
    }

    const propertyChips = Array.from(bestByToken.values())
        .sort((a, b) => (
            priorityRank(a.propertyKey) - priorityRank(b.propertyKey)
            || b.count - a.count
            || a.token.localeCompare(b.token, undefined, {sensitivity: 'base'})
        ))
        .slice(0, MAX_PROPERTY_CHIPS)
        .map((value) => ({
            id: `prop:${value.propertyKey}:${value.token}`,
            kind: 'property',
            propertyKey: value.propertyKey,
            token: value.token,
            label: formatFacetChipLabel(value.token),
            group: propertyLabelForKey(value.propertyKey, propertyOptions),
            count: value.count,
            // QID of the value entity (e.g. Q96 for Mexico) — used to load avatar
            valueEntityId: value.valueEntityId || null,
            // Resolved group label (e.g. "Instance of") used in chip tooltip
            groupLabel: PROPERTY_GROUP_LABELS[value.propertyKey] || propertyLabelForKey(value.propertyKey, propertyOptions) || value.propertyKey,
        }));

    return [...typeChips, ...propertyChips];
}
